import { useState } from "react";
import { Play, X } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal } from "../components/motion";
import { PageHero, CtaSection } from "../components/common";
import { VIDEOS, VIDEO_CATEGORIES } from "../data/site";
import { Dialog, DialogContent } from "../components/ui/dialog";

export default function Videos() {
  const [cat, setCat] = useState("All");
  const [active, setActive] = useState(null);

  const list = cat === "All" ? VIDEOS : VIDEOS.filter((v) => v.category === cat);

  return (
    <>
      <Seo
        title="STERILTRON Videos | Technologies & Engineering Solutions"
        description="Explore STERILTRON technologies, systems and engineering solutions through video — medical waste treatment, sterilization and industrial systems."
        path="/videos"
      />
      <PageHero
        label="Videos"
        titleLines={["STERILTRON Videos"]}
        description="Explore our technologies, systems and engineering solutions through video."
      />

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap gap-3 mb-12">
            {VIDEO_CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`video-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                onClick={() => setCat(c)}
                className={`px-5 py-2.5 text-xs font-semibold uppercase tracking-wider border transition-colors ${
                  cat === c ? "bg-brand-800 text-white border-brand-800" : "bg-white text-slate-600 border-slate-300 hover:border-brand-800 hover:text-brand-800"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {list.map((v, i) => (
              <Reveal key={v.id} delay={(i % 3) * 0.08}>
                <button
                  onClick={() => setActive(v)}
                  data-testid={`video-card-${v.id}`}
                  className="group block w-full text-left bg-white border border-slate-200 hover:border-brand-800 transition-colors h-full"
                >
                  <div className="relative overflow-hidden aspect-video bg-slate-900">
                    <img src={v.thumbnail} alt={v.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-brand-950/30 group-hover:bg-brand-950/20 transition-colors" />
                    <span className="absolute inset-0 flex items-center justify-center">
                      <span className="w-16 h-16 rounded-full bg-white/90 backdrop-blur flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Play size={24} className="text-brand-800 ml-1" fill="currentColor" />
                      </span>
                    </span>
                    {v.demo && (
                      <span className="absolute top-3 left-3 bg-white/90 backdrop-blur px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest text-brand-800">Demo</span>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="text-xs uppercase tracking-widest text-brand-600 font-semibold">{v.category}</div>
                    <h2 className="mt-2 font-heading text-lg font-semibold tracking-tight text-brand-950 leading-snug">{v.title}</h2>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{v.description}</p>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Video lightbox */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-4xl p-0 border-0 bg-brand-950 rounded-none overflow-hidden [&>button]:hidden">
          {active && (
            <div>
              <div className="relative aspect-video bg-black" data-testid="video-modal">
                {active.embedUrl ? (
                  <iframe
                    title={active.title}
                    src={active.embedUrl}
                    className="w-full h-full"
                    allow="acceleromer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-center px-6 bg-cover bg-center" style={{ backgroundImage: `linear-gradient(rgba(6,22,42,0.82),rgba(6,22,42,0.82)), url(${active.thumbnail})` }}>
                    <span className="w-16 h-16 rounded-full border-2 border-white/40 flex items-center justify-center mb-6">
                      <Play size={26} className="text-white ml-1" fill="currentColor" />
                    </span>
                    <p data-testid="video-placeholder" className="font-heading text-xl md:text-2xl font-bold tracking-tight text-white">VIDEO PLACEHOLDER — STERILTRON MEDIA</p>
                    <p className="mt-3 text-sm text-brand-200 max-w-md">This video will be available soon. The player is ready — the media URL can be added at any time.</p>
                  </div>
                )}
                <button
                  onClick={() => setActive(null)}
                  data-testid="video-modal-close"
                  className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 backdrop-blur flex items-center justify-center text-white transition-colors"
                  aria-label="Close video"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="p-6 md:p-8">
                <div className="text-xs uppercase tracking-widest text-brand-300 font-semibold">{active.category}</div>
                <h3 className="mt-2 font-heading text-xl md:text-2xl font-semibold tracking-tight text-white">{active.title}</h3>
                <p className="mt-2 text-sm text-brand-100 leading-relaxed">{active.description}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <CtaSection />
    </>
  );
}
